import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpacityIcon from '@mui/icons-material/Opacity';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';
import StatChip from './components/StatChip';
import useFetchApi from '../../../hooks/useFetchApi';
import useIrrigationControl from '../../../hooks/useIrrigationControl';
import useCanControlIrrigation from '../../../hooks/useCanControlIrrigation';
import { GetIrrigationsResponse } from '../../../requests/api/apiTypes';
import mapDashboardData from './dashboardMapper';
import RiegoGantt from './RiegoGantt';
import ScheduleIrrigationModal from './ScheduleIrrigationModal';
import PeakHourModal from './PeakHourModal';
import usePeakHours from './usePeakHours';
import { boldPressableSx, pressableSx, surfaceSx } from './riegoGantt.styles';

interface PeakHourModalState {
  open: boolean;
  pumpSystemId: number | null;
  pumpSystemName: string;
}

const CLOSED_PEAK_HOUR_MODAL: PeakHourModalState = {
  open: false,
  pumpSystemId: null,
  pumpSystemName: '',
};

type RangeDays = 1 | 3 | 7;

const formatRangeLabel = (day: Date, rangeDays: RangeDays): string => {
  const start = dayjs(day).startOf('day');
  if (rangeDays === 1) {
    return new Intl.DateTimeFormat('es-CL', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    }).format(start.toDate());
  }
  const end = start.add(rangeDays - 1, 'day').toDate();
  const short = new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short' });
  const year = new Intl.DateTimeFormat('es-CL', { year: 'numeric' }).format(end);
  return `${short.format(start.toDate())} – ${short.format(end)} ${year}`;
};

const DATE_LABEL_MIN_WIDTH = 260;
const QUERY_DEBOUNCE_MS = 450;
const SKELETON_ROW_COUNT = 2;
const SKELETON_LABEL_WIDTH = 220;
const SKELETON_BLOCK_HEIGHT = 180;

const RiegoDashboard = ({ farmId }: { farmId?: string }) => {
  const [day, setDay] = useState<Date>(() => new Date());
  const [rangeDays, setRangeDays] = useState<RangeDays>(1);
  const [equipmentFilter, setEquipmentFilter] = useState<number | 'all'>('all');
  const [reloadKey, setReloadKey] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [peakHourModal, setPeakHourModal] = useState<PeakHourModalState>(CLOSED_PEAK_HOUR_MODAL);
  const canControl = useCanControlIrrigation();
  const { configByPumpSystemId, refetch: refetchPeakHours } = usePeakHours(farmId);

  const { initTime, endTime } = useMemo(() => {
    const start = dayjs(day).startOf('day');
    return {
      initTime: start.toISOString(),
      endTime: start.add(rangeDays, 'day').toISOString(),
    };
  }, [day, rangeDays]);

  const [debouncedQuery, setDebouncedQuery] = useState({ initTime, endTime });

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedQuery({ initTime, endTime }),
      QUERY_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [initTime, endTime]);

  const { data: apiData, loading: isLoading } = useFetchApi<GetIrrigationsResponse>({
    id: farmId || '',
    inputData: debouncedQuery,
    dependencies: [farmId || '', debouncedQuery.initTime, debouncedQuery.endTime, reloadKey],
    modelName: 'irrigation',
    modelEndpoint: 'getIrrigations',
  });

  const data = useMemo(() => (apiData ? mapDashboardData(apiData) : null), [apiData]);

  useEffect(() => {
    setEquipmentFilter('all');
  }, [farmId]);

  const isToday = dayjs(day).isSame(dayjs(), 'day');

  const summary = useMemo(() => {
    if (!data) return null;
    return {
      equipos: data.pumpSystems.length,
      zonas: data.zones.length,
      programados: data.scheduledIrrigations.length,
      corriendo: data.realIrrigations.filter((r) => r.status === 'running').length,
      reales: data.realIrrigations.length,
    };
  }, [data]);

  const visiblePumpSystems = useMemo(() => {
    if (!data) return [];
    if (equipmentFilter === 'all') return data.pumpSystems;
    return data.pumpSystems.filter((p) => p.id === equipmentFilter);
  }, [data, equipmentFilter]);

  const { rangeStart, rangeEnd } = useMemo(() => {
    const start = dayjs(day).startOf('day');
    const end = start.add(rangeDays, 'day').subtract(1, 'millisecond');
    return { rangeStart: start.toDate(), rangeEnd: end.toDate() };
  }, [day, rangeDays]);

  const shiftDay = (delta: number) => {
    setDay((prev) => dayjs(prev).add(delta * rangeDays, 'day').toDate());
  };

  const handleRefresh = () => setReloadKey((prev) => prev + 1);

  const control = useIrrigationControl(farmId, handleRefresh);

  return (
    <Box>
      {/* Cabecera operativa */}
      <Paper
        elevation={0}
        sx={{
          ...surfaceSx,
          p: 2.5,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          gap={2.5}
        >
          {/* Izquierda: navegación de fecha + rango */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <IconButton onClick={() => shiftDay(-1)} aria-label="anterior">
                <ChevronLeftIcon />
              </IconButton>
              <Box sx={{ minWidth: DATE_LABEL_MIN_WIDTH, textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, textTransform: 'capitalize', lineHeight: 1.2 }}
                >
                  {formatRangeLabel(day, rangeDays)}
                </Typography>
                {isToday && rangeDays === 1 && (
                  <Chip label="HOY" color="primary" size="small" sx={{ mt: 0.75, fontWeight: 700 }} />
                )}
              </Box>
              <IconButton onClick={() => shiftDay(1)} aria-label="siguiente">
                <ChevronRightIcon />
              </IconButton>
              <Button
                variant="text"
                color="primary"
                onClick={() => setDay(new Date())}
                sx={boldPressableSx}
              >
                Hoy
              </Button>
            </Box>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={rangeDays}
              onChange={(_, v) => v && setRangeDays(v)}
              sx={{ alignSelf: 'flex-start', '& .MuiToggleButton-root': { textTransform: 'uppercase' } }}
            >
              <ToggleButton value={1}>Día</ToggleButton>
              <ToggleButton value={3}>3 días</ToggleButton>
              <ToggleButton value={7}>Semana</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' } }} />

          {/* Derecha: resumen + acciones */}
          <Box display="flex" flexDirection="column" alignItems={{ xs: 'stretch', lg: 'flex-end' }} gap={2} flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
              {summary && (
                <>
                  <StatChip value={summary.equipos} label="equipos" />
                  <StatChip value={summary.zonas} label="zonas" />
                  <StatChip value={summary.programados} label="programados" accent="primary" />
                  <StatChip value={summary.corriendo} label="corriendo" accent="success" />
                  <StatChip value={summary.reales} label="reales" />
                </>
              )}
              <Button
                variant="text"
                color="primary"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={isLoading}
                sx={boldPressableSx}
              >
                Refrescar
              </Button>
            </Box>
            {canControl && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<OpacityIcon />}
                onClick={() => setScheduleOpen(true)}
                disabled={!data}
                sx={pressableSx}
              >
                Programar riego
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Filtro por equipo */}
      {data && data.pumpSystems.length > 1 && (
        <Box mb={3}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={equipmentFilter}
            onChange={(_, v) => v != null && setEquipmentFilter(v)}
          >
            <ToggleButton value="all">Todos los equipos</ToggleButton>
            {data.pumpSystems.map((p) => (
              <ToggleButton key={p.id} value={p.id}>{p.name}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Skeleton de carga */}
      {isLoading && !data && (
        <Box display="flex" flexDirection="column" gap={2}>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <Box key={i}>
              <Skeleton variant="text" width={SKELETON_LABEL_WIDTH} />
              <Skeleton variant="rectangular" height={SKELETON_BLOCK_HEIGHT} />
            </Box>
          ))}
        </Box>
      )}

      {/* Estado vacío mientras no hay fuente de datos */}
      {!isLoading && !data && (
        <Alert severity="info">
          No hay datos de riego disponibles para {farmId ? 'este campo' : 'el campo seleccionado'}.
        </Alert>
      )}

      {/* Gantt por equipo */}
      {data && (
        <>
          {visiblePumpSystems.length === 0 ? (
            <Alert severity="info">No hay equipos de riego para mostrar.</Alert>
          ) : (
            visiblePumpSystems.map((p) => {
              const zones = data.zones.filter((z) => z.pumpSystemId === p.id);
              const zoneIds = new Set(zones.map((z) => z.id));
              const scheduled = data.scheduledIrrigations.filter((s) => zoneIds.has(s.zoneId));
              const real = data.realIrrigations.filter((r) => zoneIds.has(r.zoneId));
              const peakHourConfig = configByPumpSystemId.get(p.id);
              const peakHourEnabled = !!peakHourConfig?.enabled;
              const peakHourTooltip = peakHourEnabled
                ? `Hora punta activa (${peakHourConfig?.init}–${peakHourConfig?.end})`
                : 'Configurar hora punta';
              return (
                <RiegoGantt
                  key={p.id}
                  pumpSystem={p}
                  zones={zones}
                  scheduled={scheduled}
                  real={real}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  canControl={canControl}
                  isSending={control.isSending}
                  onDelete={(irrigationId, zoneId) => control.remove(irrigationId, zoneId)}
                  onStop={(irrigationId, zoneId) => control.stop(irrigationId, zoneId)}
                  onActivate={(irrigationId) => control.activate(irrigationId)}
                  peakHour={peakHourEnabled && peakHourConfig?.init && peakHourConfig?.end
                    ? { init: peakHourConfig.init, end: peakHourConfig.end }
                    : undefined}
                  headerAction={canControl && (
                    <Tooltip title={peakHourTooltip}>
                      <Button
                        size="small"
                        variant={peakHourEnabled ? 'text' : 'outlined'}
                        color="warning"
                        startIcon={<AccessTimeIcon fontSize="small" />}
                        onClick={() => setPeakHourModal({
                          open: true,
                          pumpSystemId: p.id,
                          pumpSystemName: p.name,
                        })}
                        sx={{ ...boldPressableSx, letterSpacing: '0.03em' }}
                      >
                        Hora punta
                      </Button>
                    </Tooltip>
                  )}
                />
              );
            })
          )}
        </>
      )}

      {data && (
        <ScheduleIrrigationModal
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          pumpSystems={data.pumpSystems}
          zones={data.zones}
          isSending={control.isSending}
          onSubmit={control.start}
        />
      )}

      {farmId && peakHourModal.pumpSystemId !== null && (
        <PeakHourModal
          open={peakHourModal.open}
          onClose={() => setPeakHourModal(CLOSED_PEAK_HOUR_MODAL)}
          onSaved={refetchPeakHours}
          farmId={farmId}
          pumpSystemId={peakHourModal.pumpSystemId}
          pumpSystemName={peakHourModal.pumpSystemName}
          current={configByPumpSystemId.get(peakHourModal.pumpSystemId)}
        />
      )}
    </Box>
  );
};

export default RiegoDashboard;
